import { useState, useEffect, useRef } from 'react'
import Box from '@mui/joy/Box'
import Stack from '@mui/joy/Stack'
import Sheet from '@mui/joy/Sheet'
import Typography from '@mui/joy/Typography'
import Chip from '@mui/joy/Chip'
import Button from '@mui/joy/Button'
import IconButton from '@mui/joy/IconButton'
import Tooltip from '@mui/joy/Tooltip'
import Select from '@mui/joy/Select'
import Option from '@mui/joy/Option'
import Input from '@mui/joy/Input'
import Modal from '@mui/joy/Modal'
import ModalDialog from '@mui/joy/ModalDialog'
import ModalClose from '@mui/joy/ModalClose'
import DialogTitle from '@mui/joy/DialogTitle'
import DialogContent from '@mui/joy/DialogContent'
import Divider from '@mui/joy/Divider'
import CircularProgress from '@mui/joy/CircularProgress'
import Alert from '@mui/joy/Alert'

import BusinessCenterIcon from '@mui/icons-material/BusinessCenter'
import AddIcon from '@mui/icons-material/Add'
import CloseIcon from '@mui/icons-material/Close'
import PlayArrowIcon from '@mui/icons-material/PlayArrow'
import TelegramIcon from '@mui/icons-material/Telegram'
import EmailIcon from '@mui/icons-material/Email'
import PhoneInTalkIcon from '@mui/icons-material/PhoneInTalk'
import VideoCallIcon from '@mui/icons-material/VideoCall'
import StorageIcon from '@mui/icons-material/Storage'
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome'
import MemoryIcon from '@mui/icons-material/Memory'
import AccountTreeIcon from '@mui/icons-material/AccountTree'
import ScheduleIcon from '@mui/icons-material/Schedule'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'

import { triggerProcess, getPipelineConfig, setPipelineConfig, PipelineConfig } from 'api/oracleApi'
import { useProcessJobContext } from '../hooks/ProcessJobContext'
import { ProcessJobCard } from './ProcessJobCard'
import { AttioPanel } from '../datasource/ds-attio/AttioPanel'
import { TelegramDedicatedPanel } from '../datasource/ds-tg-dedicated/TelegramDedicatedPanel'
import { TelegramCustomerChannelsPanel } from '../datasource/ds-tg-customer/TelegramCustomerChannelsPanel'
import { RingCentralPanel } from '../datasource/ds-ringcentral/RingCentralPanel'
import { GoogleMeetPanel } from '../datasource/ds-gmeet/components/GoogleMeetPanel'
import { useUrlPathSegment } from '../hooks/useUrlPathSegment'

interface SourceDef {
    key: string
    label: string
    description: string
    icon: React.ReactNode
    implemented: boolean
}

const ALL_SOURCES: SourceDef[] = [
    {
        key: 'telegram-dedicated',
        label: 'Telegram Dedicated Channel',
        description: 'Forward recordings and voice memos to a single dedicated channel',
        icon: <TelegramIcon sx={{ fontSize: 16 }} />,
        implemented: true,
    },
    {
        key: 'telegram-customer',
        label: 'Telegram Customer Channels',
        description: 'Per-customer channels — add the bot, map to a company, track all messages',
        icon: <TelegramIcon sx={{ fontSize: 16 }} />,
        implemented: true,
    },
    {
        key: 'ringcentral',
        label: 'RingCentral',
        description: 'Import call transcripts, voicemails, and call logs from RingCentral',
        icon: <PhoneInTalkIcon sx={{ fontSize: 16 }} />,
        implemented: true,
    },
    {
        key: 'gmeet',
        label: 'Google Meet',
        description: 'Import transcripts, metadata, and chat messages from Google Meet meetings',
        icon: <VideoCallIcon sx={{ fontSize: 16 }} />,
        implemented: true,
    },
    {
        key: 'email',
        label: 'Email',
        description: 'Inbound and outbound emails from connected mailboxes',
        icon: <EmailIcon sx={{ fontSize: 16 }} />,
        implemented: false,
    },
    {
        key: 'attio',
        label: 'Attio',
        description: 'Call transcripts and notes from the Attio CRM (requires ATTIO_API_KEY)',
        icon: <BusinessCenterIcon sx={{ fontSize: 16 }} />,
        implemented: true,
    },
]

const SCHEDULE_OPTIONS = [
    { value: '30m', label: 'Every 30 minutes' },
    { value: '1h', label: 'Every hour' },
    { value: '2h', label: 'Every 2 hours' },
    { value: '6h', label: 'Every 6 hours' },
    { value: '12h', label: 'Every 12 hours' },
    { value: '24h', label: 'Every 24 hours' },
    { value: 'manual', label: 'Manual only' },
]

type SaveState = 'idle' | 'saving' | 'saved' | 'error'

// Workspace-shared pipeline config (Firestore-backed at workspace_config/pipeline).
// Hydrates from the API on mount; auto-saves on change with a 600ms debounce so
// no Save button is needed. localStorage is used only as a synchronous render
// hint to avoid a flash of defaults before the hydrate completes — it is NOT
// authoritative; the server response always wins once it arrives.
function usePipelineConfig() {
    const cached = (() => {
        try {
            return JSON.parse(localStorage.getItem('oracle_pipeline_config') ?? 'null') as PipelineConfig | null
        } catch {
            return null
        }
    })()
    const [activeSources, setActiveSources] = useState<string[]>(cached?.activeSources ?? [])
    const [schedule, setSchedule] = useState<string>(cached?.schedule ?? '2h')
    const [batchLimit, setBatchLimit] = useState<number>(cached?.batchLimit ?? 200)
    const [hydrated, setHydrated] = useState(false)
    const [saveState, setSaveState] = useState<SaveState>('idle')
    const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)
    const savedSavedTimeout = useRef<ReturnType<typeof setTimeout> | null>(null)

    // Hydrate from server once on mount.
    useEffect(() => {
        let cancelled = false
        void (async () => {
            try {
                const cfg = await getPipelineConfig()
                if (cancelled) return
                setActiveSources(cfg.activeSources)
                setSchedule(cfg.schedule)
                setBatchLimit(cfg.batchLimit)
                localStorage.setItem('oracle_pipeline_config', JSON.stringify(cfg))
            } catch {
                // Server unreachable — fall back to whatever was already in state from
                // the localStorage hint, and let the next change attempt re-sync.
            } finally {
                if (!cancelled) setHydrated(true)
            }
        })()
        return () => {
            cancelled = true
        }
    }, [])

    // Auto-save with 600ms debounce. Skipped while hydrating to avoid a phantom
    // round-trip that would write back the cached/defaulted values.
    useEffect(() => {
        if (!hydrated) return
        if (debounceRef.current) clearTimeout(debounceRef.current)
        debounceRef.current = setTimeout(() => {
            const next: PipelineConfig = { activeSources, schedule, batchLimit, sourceConfigs: {} }
            setSaveState('saving')
            void setPipelineConfig(next)
                .then((merged) => {
                    localStorage.setItem('oracle_pipeline_config', JSON.stringify(merged))
                    setSaveState('saved')
                    if (savedSavedTimeout.current) clearTimeout(savedSavedTimeout.current)
                    savedSavedTimeout.current = setTimeout(() => setSaveState('idle'), 1500)
                })
                .catch(() => setSaveState('error'))
        }, 600)
        return () => {
            if (debounceRef.current) clearTimeout(debounceRef.current)
        }
    }, [activeSources, schedule, batchLimit, hydrated])

    return { activeSources, setActiveSources, schedule, setSchedule, batchLimit, setBatchLimit, hydrated, saveState }
}

function SaveStateIndicator({ state, hydrated }: { state: SaveState; hydrated: boolean }) {
    if (!hydrated) {
        return (
            <Typography level="body-xs" sx={{ color: 'text.tertiary', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <CircularProgress size="sm" sx={{ '--CircularProgress-size': '12px' }} />
                Loading…
            </Typography>
        )
    }
    if (state === 'saving') {
        return (
            <Typography level="body-xs" sx={{ color: 'text.tertiary', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <CircularProgress size="sm" sx={{ '--CircularProgress-size': '12px' }} />
                Saving…
            </Typography>
        )
    }
    if (state === 'saved')
        return (
            <Typography level="body-xs" sx={{ color: 'success.500' }}>
                Saved
            </Typography>
        )
    if (state === 'error')
        return (
            <Typography level="body-xs" sx={{ color: 'danger.500' }}>
                Save failed
            </Typography>
        )
    return null
}

function ArrowConnector() {
    return (
        <Stack alignItems="center" justifyContent="center" sx={{ px: 1, flexShrink: 0 }}>
            <ChevronRightIcon sx={{ fontSize: 28, color: 'text.tertiary' }} />
        </Stack>
    )
}

function NodeShell({
    label,
    color,
    icon,
    children,
}: {
    label: string
    color?: 'primary' | 'neutral' | 'success'
    icon: React.ReactNode
    children: React.ReactNode
}) {
    const borderColor = color === 'primary' ? 'primary.300' : color === 'success' ? 'success.300' : 'divider'
    const headerBg = color === 'primary' ? 'primary.softBg' : color === 'success' ? 'success.softBg' : 'background.level1'
    return (
        <Sheet
            variant="outlined"
            sx={{
                borderRadius: 'md',
                overflow: 'hidden',
                borderColor,
                minWidth: 220,
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
            }}
        >
            <Stack direction="row" alignItems="center" gap={0.75} sx={{ px: 1.5, py: 1, bgcolor: headerBg, borderBottom: '1px solid', borderColor }}>
                {icon}
                <Typography level="body-xs" fontWeight="lg" sx={{ textTransform: 'uppercase', letterSpacing: '0.05em', fontSize: 11 }}>
                    {label}
                </Typography>
            </Stack>
            <Box sx={{ p: 1.5, flex: 1 }}>{children}</Box>
        </Sheet>
    )
}

function DataSourcesNode({
    activeSources,
    onAdd,
    onRemove,
    onOpenDetail,
}: {
    activeSources: string[]
    onAdd: (key: string) => void
    onRemove: (key: string) => void
    onOpenDetail: (key: string) => void
}) {
    const [addOpen, setAddOpen] = useState(false)
    const available = ALL_SOURCES.filter((s) => !activeSources.includes(s.key))

    return (
        <>
            <NodeShell label="Node 1 · DataSources" color="primary" icon={<AccountTreeIcon sx={{ fontSize: 14, color: 'primary.500' }} />}>
                <Stack gap={0.75}>
                    {activeSources.map((key) => {
                        const def = ALL_SOURCES.find((s) => s.key === key)
                        if (!def) return null
                        const hasDetail = ['attio', 'telegram-dedicated', 'telegram-customer', 'ringcentral', 'gmeet'].includes(key)
                        return (
                            <Sheet
                                key={key}
                                variant="soft"
                                color="primary"
                                onClick={hasDetail ? () => onOpenDetail(key) : undefined}
                                sx={{
                                    borderRadius: 'sm',
                                    px: 1,
                                    py: 0.75,
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 0.75,
                                    cursor: hasDetail ? 'pointer' : 'default',
                                    transition: 'background-color 120ms',
                                    '&:hover': hasDetail ? { bgcolor: 'primary.softHoverBg' } : {},
                                }}
                            >
                                {def.icon}
                                <Typography level="body-xs" fontWeight="md" sx={{ flex: 1 }}>
                                    {def.label}
                                </Typography>
                                <Chip size="sm" variant="solid" color="primary" sx={{ fontSize: 9, px: 0.5, minHeight: 16 }}>
                                    active
                                </Chip>
                                <Tooltip title="Remove source">
                                    <IconButton
                                        size="sm"
                                        variant="plain"
                                        color="neutral"
                                        onClick={(e) => {
                                            e.stopPropagation()
                                            onRemove(key)
                                        }}
                                        sx={{ minHeight: 20, minWidth: 20 }}
                                    >
                                        <CloseIcon sx={{ fontSize: 11 }} />
                                    </IconButton>
                                </Tooltip>
                            </Sheet>
                        )
                    })}
                    {activeSources.length === 0 && (
                        <Typography level="body-xs" color="neutral" sx={{ fontStyle: 'italic' }}>
                            No sources active
                        </Typography>
                    )}
                    {available.length > 0 && (
                        <Button
                            size="sm"
                            variant="outlined"
                            color="primary"
                            startDecorator={<AddIcon sx={{ fontSize: 13 }} />}
                            onClick={() => setAddOpen(true)}
                            sx={{ mt: 0.25, fontSize: 11 }}
                        >
                            Add source
                        </Button>
                    )}
                </Stack>
            </NodeShell>

            <Modal open={addOpen} onClose={() => setAddOpen(false)}>
                <ModalDialog sx={{ maxWidth: 400 }}>
                    <ModalClose />
                    <DialogTitle>Add a DataSource</DialogTitle>
                    <DialogContent>
                        <Stack gap={1} sx={{ pt: 0.5 }}>
                            {available.map((def) => (
                                <Sheet
                                    key={def.key}
                                    variant="outlined"
                                    sx={{
                                        borderRadius: 'sm',
                                        p: 1.5,
                                        cursor: def.implemented ? 'pointer' : 'default',
                                        opacity: def.implemented ? 1 : 0.55,
                                        '&:hover': def.implemented ? { bgcolor: 'background.level2' } : {},
                                    }}
                                    onClick={() => {
                                        if (!def.implemented) return
                                        onAdd(def.key)
                                        setAddOpen(false)
                                    }}
                                >
                                    <Stack direction="row" alignItems="flex-start" gap={1}>
                                        <Box sx={{ mt: 0.25 }}>{def.icon}</Box>
                                        <Stack gap={0.25} flex={1}>
                                            <Stack direction="row" alignItems="center" gap={0.75}>
                                                <Typography level="title-sm">{def.label}</Typography>
                                                {!def.implemented && (
                                                    <Chip size="sm" variant="soft" color="neutral" sx={{ fontSize: 9, px: 0.5, minHeight: 16 }}>
                                                        coming soon
                                                    </Chip>
                                                )}
                                            </Stack>
                                            <Typography level="body-xs" color="neutral">
                                                {def.description}
                                            </Typography>
                                        </Stack>
                                    </Stack>
                                </Sheet>
                            ))}
                        </Stack>
                    </DialogContent>
                </ModalDialog>
            </Modal>
        </>
    )
}

function ProcessingNode() {
    const steps = [
        { n: 1, label: 'Extract', detail: 'LLM extracts typed facts and contacts per company' },
        { n: 2, label: 'Match', detail: 'Resolves company names → company IDs (5-pass cascade)' },
        { n: 3, label: 'Reconcile', detail: 'Detects contradictions, computes supersessions' },
    ]
    return (
        <NodeShell label="Node 2 · Processing" color="neutral" icon={<MemoryIcon sx={{ fontSize: 14, color: 'text.secondary' }} />}>
            <Stack gap={0.75}>
                {steps.map((s) => (
                    <Tooltip key={s.n} title={s.detail} placement="top">
                        <Sheet variant="soft" sx={{ borderRadius: 'sm', px: 1, py: 0.75, cursor: 'default' }}>
                            <Stack direction="row" alignItems="center" gap={0.75}>
                                <Chip size="sm" variant="outlined" sx={{ fontSize: 9, px: 0.5, minHeight: 16, minWidth: 16, textAlign: 'center' }}>
                                    {s.n}
                                </Chip>
                                <Typography level="body-xs" fontWeight="md">
                                    {s.label}
                                </Typography>
                            </Stack>
                        </Sheet>
                    </Tooltip>
                ))}
                <Typography level="body-xs" color="neutral" sx={{ fontSize: 10, pt: 0.25 }}>
                    Source-agnostic · read-only · emits CommitPlan
                </Typography>
            </Stack>
        </NodeShell>
    )
}

function StorageNode() {
    return (
        <NodeShell label="Node 3 · Storage" color="success" icon={<StorageIcon sx={{ fontSize: 14, color: 'success.500' }} />}>
            <Stack gap={0.75}>
                <Sheet variant="soft" color="success" sx={{ borderRadius: 'sm', px: 1, py: 0.75 }}>
                    <Stack direction="row" alignItems="center" gap={0.75}>
                        <StorageIcon sx={{ fontSize: 13, color: 'success.600' }} />
                        <Stack gap={0.1}>
                            <Typography level="body-xs" fontWeight="md">
                                Firestore DB
                            </Typography>
                            <Typography level="body-xs" color="neutral" sx={{ fontSize: 10 }}>
                                crm_companies · crm_facts · crm_contacts
                            </Typography>
                        </Stack>
                    </Stack>
                </Sheet>
                <Sheet variant="soft" color="success" sx={{ borderRadius: 'sm', px: 1, py: 0.75 }}>
                    <Stack direction="row" alignItems="center" gap={0.75}>
                        <AutoAwesomeIcon sx={{ fontSize: 13, color: 'success.600' }} />
                        <Stack gap={0.1}>
                            <Typography level="body-xs" fontWeight="md">
                                Gemini File Search
                            </Typography>
                            <Typography level="body-xs" color="neutral" sx={{ fontSize: 10 }}>
                                RAG index · one doc per active fact
                            </Typography>
                        </Stack>
                    </Stack>
                </Sheet>
                <Typography level="body-xs" color="neutral" sx={{ fontSize: 10, pt: 0.25 }}>
                    No LLM · applies CommitPlan · append-only facts
                </Typography>
            </Stack>
        </NodeShell>
    )
}

export function PipelineConstructorTab() {
    const { activeSources, setActiveSources, schedule, setSchedule, batchLimit, setBatchLimit, hydrated, saveState } = usePipelineConfig()
    const [running, setRunning] = useState(false)
    const [runError, setRunError] = useState('')
    const [openSource, setOpenSource] = useUrlPathSegment(0)

    const { job, isCancelling, cancelJob, dismissJob } = useProcessJobContext()
    const showJobCard = !!job && !job.source_filter

    if (openSource === 'attio') return <AttioPanel onBack={() => setOpenSource(null)} />
    if (openSource === 'telegram-dedicated') return <TelegramDedicatedPanel onBack={() => setOpenSource(null)} />
    if (openSource === 'telegram-customer') return <TelegramCustomerChannelsPanel onBack={() => setOpenSource(null)} />
    if (openSource === 'ringcentral') return <RingCentralPanel onBack={() => setOpenSource(null)} />
    if (openSource === 'gmeet') return <GoogleMeetPanel onBack={() => setOpenSource(null)} />

    const addSource = (key: string) => {
        if (activeSources.includes(key)) return
        setActiveSources([...activeSources, key])
    }

    const removeSource = (key: string) => {
        setActiveSources(activeSources.filter((k) => k !== key))
    }

    const handleRunNow = async () => {
        setRunning(true)
        setRunError('')
        try {
            await triggerProcess()
        } catch (err) {
            setRunError(err instanceof Error ? err.message : String(err))
        } finally {
            setRunning(false)
        }
    }

    return (
        <Stack gap={2}>
            {/* Header */}
            <Stack direction="row" alignItems="center" justifyContent="space-between">
                <Stack gap={0.15}>
                    <Typography level="title-sm">Pipeline Constructor</Typography>
                    <Typography level="body-xs" color="neutral">
                        Configure data sources and execution schedule
                    </Typography>
                </Stack>
                <Stack direction="row" gap={1} alignItems="center">
                    <SaveStateIndicator state={saveState} hydrated={hydrated} />
                    <Button
                        size="sm"
                        variant="solid"
                        color="primary"
                        startDecorator={running ? <CircularProgress size="sm" /> : <PlayArrowIcon />}
                        disabled={running || activeSources.length === 0}
                        onClick={() => void handleRunNow()}
                    >
                        Run now
                    </Button>
                </Stack>
            </Stack>

            {runError && (
                <Alert color="danger" size="sm">
                    {runError}
                </Alert>
            )}
            {showJobCard && job && <ProcessJobCard job={job} isCancelling={isCancelling} onCancel={cancelJob} onDismiss={dismissJob} />}

            {/* Visual pipeline */}
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'stretch',
                    gap: 0,
                    overflowX: 'auto',
                    pb: 1,
                }}
            >
                <DataSourcesNode activeSources={activeSources} onAdd={addSource} onRemove={removeSource} onOpenDetail={setOpenSource} />
                <ArrowConnector />
                <ProcessingNode />
                <ArrowConnector />
                <StorageNode />
            </Box>

            <Divider />

            {/* Schedule & config */}
            <Stack gap={1.5}>
                <Stack direction="row" alignItems="center" gap={0.75}>
                    <ScheduleIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                    <Typography level="title-sm">Execution schedule</Typography>
                </Stack>
                <Stack direction={{ xs: 'column', sm: 'row' }} gap={2} alignItems="flex-start">
                    <Stack gap={0.5}>
                        <Typography level="body-xs" color="neutral">
                            Run interval
                        </Typography>
                        <Select
                            size="sm"
                            value={schedule}
                            onChange={(_, v) => {
                                if (v) setSchedule(v)
                            }}
                            sx={{ minWidth: 180 }}
                        >
                            {SCHEDULE_OPTIONS.map((o) => (
                                <Option key={o.value} value={o.value}>
                                    {o.label}
                                </Option>
                            ))}
                        </Select>
                    </Stack>
                    <Stack gap={0.5}>
                        <Typography level="body-xs" color="neutral">
                            Batch limit per source
                        </Typography>
                        <Input
                            size="sm"
                            type="number"
                            value={batchLimit}
                            onChange={(e) => {
                                const v = parseInt(e.target.value, 10)
                                if (!isNaN(v) && v > 0) setBatchLimit(v)
                            }}
                            slotProps={{ input: { min: 1, max: 5000 } }}
                            sx={{ width: 100 }}
                        />
                    </Stack>
                    <Stack gap={0.5}>
                        <Typography level="body-xs" color="neutral">
                            Active sources
                        </Typography>
                        <Chip size="sm" variant="soft" color={activeSources.length > 0 ? 'primary' : 'neutral'}>
                            {activeSources.length} / {ALL_SOURCES.filter((s) => s.implemented).length} implemented
                        </Chip>
                    </Stack>
                </Stack>
                {schedule !== 'manual' && (
                    <Typography level="body-xs" color="neutral">
                        Cloud Scheduler triggers <code>POST /v1/process</code> with batch limit {batchLimit}. Changes to schedule interval require a Cloud
                        Scheduler update in GCP.
                    </Typography>
                )}
            </Stack>
        </Stack>
    )
}
